/**
 * @voltxampere/web — Accessibility preferences (Sprint 80.Z B1 +
 * L-AYARLAR-2 reduced-motion extension).
 *
 * Closes Sprint 24 backlog "S25-A11Y-HIGH-CONTRAST-UI-BINDING"
 * (Sprint 80.Z B1) plus the L-AYARLAR-2 audit finding
 * (reducedMotion toggle placeholder).
 *
 * Pattern (canonical): signal + setter + IDB persist + init
 * hydrate + main.ts effect toggling `document.body.classList`.
 *
 * Persistence schema lives at `packages/persistence/src/types.ts`
 * — `A11ySettings.{highContrast, reducedMotion, fontSize?}`.
 *
 * IMPORTANT: `persist()` MUST round-trip the whole `a11y` block
 * (read existing first, then merge in the changed field) — the
 * pre-L-AYARLAR-2 version hardcoded `reducedMotion: false` AND
 * silently stomped any persisted fontSize. Each setter now reads
 * the current state of the OTHER signals before writing.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

import { fontSize, FONT_SIZES } from "./font-size-state.js";

export const highContrast = signal<boolean>(false);
export const reducedMotion = signal<boolean>(false);

let initialised = false;

export function setHighContrast(value: boolean): void {
  if (highContrast.value === value) return;
  highContrast.value = value;
  void persist();
}

export function toggleHighContrast(): void {
  setHighContrast(!highContrast.value);
}

export function setReducedMotion(value: boolean): void {
  if (reducedMotion.value === value) return;
  reducedMotion.value = value;
  void persist();
}

export function toggleReducedMotion(): void {
  setReducedMotion(!reducedMotion.value);
}

async function persist(): Promise<void> {
  try {
    await mergeSettings({
      a11y: {
        highContrast: highContrast.value,
        reducedMotion: reducedMotion.value,
        fontSize: fontSize.value,
      },
    });
  } catch {
    // ignore — soft-fail on private mode / quota exceeded
  }
}

/**
 * Boot-time hydration. Reads `getSettings().a11y` and seeds all
 * three a11y signals (highContrast, reducedMotion, fontSize).
 * Idempotent — safe to call multiple times.
 *
 * Falls back to the OS-level `prefers-reduced-motion` media query
 * when no explicit user preference is persisted yet, so the first
 * boot honours the system setting without forcing the user to
 * flip the toggle manually.
 */
export async function initA11yPrefs(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    const a11y = settings?.a11y;
    if (a11y) {
      highContrast.value = a11y.highContrast;
      reducedMotion.value = a11y.reducedMotion;
      const persistedFontSize = a11y.fontSize;
      if (persistedFontSize && FONT_SIZES.includes(persistedFontSize)) {
        fontSize.value = persistedFontSize;
      }
    } else if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      reducedMotion.value = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  } catch {
    // ignore
  }
}
